import { Typography } from "antd";

import { useWidgetData } from "../Contexts/dashboardHooks";
import { resolveDateRange } from "../Utils/resolveDateRange";
import type { RadarComponentSpec } from "../Processing/types";

const { Text } = Typography;

const VERTEX_DIMENSION_LABEL: Record<string, string> = {
  player: "Players",
  position: "Positions",
  drill: "Drills",
};

export function RadarInfo({ id }: { id: string }) {
  const data = useWidgetData(id);
  if (!data || data.type !== "radar") return null;
  const spec = data.spec as RadarComponentSpec;

  const misc = spec.radar_builder_misc ?? {};
  const vertexDimension = spec.vertexDimension ?? (misc.vertexDimension as string | undefined) ?? "metric";
  // The shared resolver, not a preset lookup: this used to read the preset even when the
  // widget was on a custom range, so the label named a window the radar was not using.
  const dateLabel = resolveDateRange(misc);

  const scaleLabel =
    spec.normalization === "player_minmax" ? "% Of Max"
    : spec.normalization === "team_zscore" ? "Relative"
    : vertexDimension === "metric" ? "Custom (per metric)"
    : `Custom (${spec.customMin ?? 0}–${spec.customMax ?? 100})`;

  const rows: { label: string; value: string }[] = [
    { label: "Date Range", value: dateLabel },
    { label: "Scale", value: scaleLabel },
  ];

  // spec.series are the subject entities; spec.metric_names are the metric labels
  // already carrying their aggregation prefix ([A]/[S]/[B]) — the same labels drawn
  // on the chart. Show both regardless of which dimension sits on the spokes.
  const subjectNames = (spec.series ?? [])
    .map((s) => s.displayNameOverride ?? s.displayName)
    .filter(Boolean);
  const metricNames = (spec.metric_names ?? []).filter(Boolean);

  const subjectLabel =
    vertexDimension === "metric" ? "Subjects" : (VERTEX_DIMENSION_LABEL[vertexDimension] ?? "Subjects");

  if (subjectNames.length > 0) {
    rows.push({ label: subjectLabel, value: subjectNames.join(", ") });
  }
  if (metricNames.length > 0) {
    rows.push({ label: metricNames.length === 1 ? "Metric" : "Metrics", value: metricNames.join(", ") });
  }

  return (
    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
      {rows.map(({ label, value }) => (
        <div key={label}>
          <Text type="secondary">{label}: </Text>
          <Text>{value}</Text>
        </div>
      ))}
    </div>
  );
}
